import { useEffect, useState } from "react";
import { Mic, Youtube, Facebook, Music2, Instagram, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/app/widgets";

const ICONS = { podcast: Mic, youtube: Youtube, facebook: Facebook, tiktok: Music2, instagram: Instagram };

export default function Engines() {
  const [engines, setEngines] = useState(null);

  useEffect(() => {
    api.get("/engines").then((r) => setEngines(r.data.engines)).catch(() => setEngines({}));
  }, []);

  if (!engines) return <Spinner label="Loading engines" />;

  return (
    <div data-testid="engines-page">
      <PageHeader
        title="Acquisition Engines"
        sub="Five specialized engines, one Central Distribution Core — same intelligence, CRM, scoring, outreach and learning underneath."
      />
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {Object.entries(engines).map(([key, e]) => {
          const Icon = ICONS[key] || Mic;
          return (
            <div key={key} data-testid={`engine-status-${key}`} className="border border-[#27272A] bg-[#0A0A0A] p-6 transition-colors hover:border-[#D4AF37]/40">
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5">
                  <Icon className="h-4.5 w-4.5 h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-wider ${
                  e.status === "live" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
                }`}>
                  {e.status === "live" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  {e.status}
                </span>
              </div>
              <h3 className="mt-4 font-serif text-xl font-semibold">{e.name}</h3>
              <div className="mt-3 space-y-1.5 text-xs text-zinc-500">
                <p><span className="text-zinc-300">Discovery:</span> {e.discovery}</p>
                <p><span className="text-zinc-300">Research:</span> {e.research}</p>
                <p><span className="text-zinc-300">Official API:</span> {e.official_api}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5 border-t border-white/5 pt-4">
                {e.capabilities.map((c) => (
                  <span key={c} className="rounded-full border border-white/10 px-2 py-0.5 text-[9px] uppercase tracking-wider text-zinc-500">
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-8 max-w-2xl text-sm leading-relaxed text-zinc-500">
        "Assisted" engines work today via URL import, public metadata research and full AI intelligence
        (score, match, value-first, blueprint, DM strategy, follow-up). Official API discovery activates
        as platform approvals are granted — the core never changes.
      </p>
    </div>
  );
}
